import BigNumber from 'bignumber.js'
import {
  DEFAULT_SOLANA_RPC,
  fetchJupiterPrices,
  fetchSolanaWalletTokens,
  getJupiterTokens,
  getSolanaMintByTarget,
  getSolanaQuote,
  getSolanaSwapTransaction,
  getSolanaTokenBalance,
} from '../../coinLiquidation/solanaUtils'
import { BaseAccountController, TAccountControllerConfig } from './BaseAccountController'
import { cacheTokens, getCachedTokens } from '../../utils/tokenCache'
import {
  BuyBackItem,
  LiquidationAsset,
  NetworkConfig,
  TargetCoinLiquidation,
} from '../../coinLiquidation/types'
import { addBuyBackItem, updateBuyBackItemTxHash } from '../../coinLiquidation/buybackUtils'
import { DEFAULT_ALLOWED_SLIPPAGE } from '../../coinLiquidation/constants'
import {
  AddressLookupTableAccount,
  Connection,
  PublicKey,
  SystemProgram,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import { WalletTokenState } from '../../coinLiquidation/walletTokenFetcher'
import { Buffer } from 'buffer'
import { getRefCode } from '@/utils/localStorage'
import { getPartnerInfoFromJSON } from '@/coinLiquidation/swapUtils'
import { sha256 } from '@noble/hashes/sha2.js'

const jupiterRefProgramId = 'REFER4ZgmyYx9c6He5XfaTMiGfdLwRnkV4RPp9t9iF3'
const jupiterProjectPublicKey = '45ruCyfdRkWpRNGEqWzjCiXRHkZs8WXCLQ67Pnpye7Hp'

const DEFAULT_SLIPPAGE_BPS = Math.floor(DEFAULT_ALLOWED_SLIPPAGE * 10000)
type TRefFee = {
  account: PublicKey
  bps: number
  tokenMint: string
}

export class SolAccountController implements BaseAccountController {
  accountState: WalletTokenState | null
  private address: string
  private _config: TAccountControllerConfig

  constructor(_address: string, config: TAccountControllerConfig) {
    this.address = _address
    this.loadCache = this.loadCache.bind(this)
    this.loadCache()
    this._config = config
  }

  updateConfig(config: TAccountControllerConfig): void {
    this._config = config
  }

  async loadAccountTokens(address: string, callback: (progress: any) => void) {
    const walletState = await fetchSolanaWalletTokens(address, this._config.solanaRpcUrl, callback)
    this.accountState = walletState
    if (walletState) {
      cacheTokens(address, walletState)
    }
    return this.accountState
  }

  async getSourceAmountByLiquidationType({
    inputToken,
    network,
  }: {
    inputToken: LiquidationAsset
    network: NetworkConfig
  }) {
    const { balance, decimals } = await getSolanaTokenBalance(
      this._config.account,
      inputToken,
      this._config.solanaRpcUrl,
    )
    console.log('balance, decimals', { balance, decimals })
    const balanceAmount = new BigNumber(balance).dividedBy(10 ** decimals).toFixed()
    return balanceAmount.toString()
    // const amount =
    //   this._config.typeLiquidation === TypeLiquidation.GLOBAL
    //     ? (this._config.globalLiquidationPercentage / 100) * Number(inputToken.USDBalance)
    //     : Number(inputToken.price)
    //
    // if (Number(inputToken.USDBalance) === 0) {
    //   return balanceAmount
    // }
    //
    // const sourceAmount = inputToken.price
    //   ? (amount * Number(balanceAmount)) /
    //     (Number(inputToken.USDBalance) || Number(inputToken.stringUSDBalance))
    //   : balanceAmount
    //
    // if (inputToken.isNative) {
    //   const usdValue = Number(inputToken.USDBalance) || 0
    //   let lockAmount = 0.5
    //   try {
    //     if (typeof window !== 'undefined') {
    //       const saved = localStorage.getItem('native_lock_usd')
    //       const parsed = saved ? Number(saved) : 0.5
    //       lockAmount = isNaN(parsed) || parsed < 0 ? 0.5 : parsed
    //     }
    //   } catch (_) {}
    //   const availableAmount = usdValue - lockAmount
    //
    //   if (availableAmount <= 0) {
    //     this._config.onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
    //     return
    //   }
    //
    //   const sellPercentage = availableAmount / usdValue
    //   const actualSellAmount = Number(sourceAmount) * sellPercentage
    //
    //   return actualSellAmount.toFixed(9)
    // }
    //
    // if (!sourceAmount || Number(sourceAmount) <= 0) {
    //   this._config.onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
    //   return
    // }
    //
    // if (Number(inputToken.price || inputToken.USDBalance) > Number(inputToken.USDBalance)) {
    //   this._config.onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
    //   return
    // }
    //
    // return sourceAmount.toString()
  }

  async onSwapToken({
    sourceToken,
    network,
  }: {
    sourceToken: LiquidationAsset
    network: NetworkConfig
  }) {
    try {
      const inputMint = sourceToken.address
      const outputMint = getSolanaMintByTarget(this._config.targetCoin)
      if (!outputMint) {
        return
      }

      const sourceAmount = await this.getSourceAmountByLiquidationType({
        inputToken: sourceToken,
        network,
      })

      if (!sourceAmount) {
        return
      }

      const amountInBaseUnits = new BigNumber(sourceAmount)
        .multipliedBy(10 ** sourceToken.decimals)
        .integerValue(BigNumber.ROUND_FLOOR)
        .toFixed(0)

      if (!amountInBaseUnits || amountInBaseUnits === '0') {
        this._config.onHandleError(`${network.chainId}.${sourceToken.symbol}`, 'insufficientAmount')
        return
      }

      const feeInfo = this.getFeeAccount(outputMint)

      const quote = await getSolanaQuote({
        inputMint,
        outputMint,
        amount: amountInBaseUnits,
        slippageBps: DEFAULT_SLIPPAGE_BPS,
        platformFeeBps: feeInfo.bps,
      })

      if (!quote) {
        return
      }
      const swapResponse = await getSolanaSwapTransaction({
        quote,
        userPublicKey: this._config.account,
        feeAccount: feeInfo.account,
      })
      const signature = await this.signAndSendSolanaTransaction(swapResponse, feeInfo)
      const tokenMetadata = await getJupiterTokens()
      const outputMeta = tokenMetadata.find((token) => token.address === outputMint)
      const outputToken = {
        symbol: outputMeta?.symbol || sourceToken.symbol,
        name: outputMeta?.name || sourceToken.name,
        address: outputMint,
        decimals: outputMeta?.decimals || sourceToken.decimals,
        isNative: this._config.targetCoin === TargetCoinLiquidation.NATIVE,
      }

      const prices = await fetchJupiterPrices([inputMint, outputMint])
      const inputPrice = prices[inputMint.toLowerCase()] || 0
      const outputPrice = prices[outputMint.toLowerCase()] || 0

      const destAmount = new BigNumber(quote.outAmount)
        .dividedBy(10 ** outputToken.decimals)
        .toFixed()

      return {
        success: true,
        txHash: signature,
        inputToken: sourceToken,
        outputToken,
        network,
        sourceAmount,
        destAmount,
        srcUSD: Number(sourceAmount) * inputPrice,
        destUSD: Number(destAmount) * outputPrice,
      }
    } catch (error: any) {
      console.log('error', error)
      this._config.onHandleError(
        `${network.chainId}.${sourceToken.symbol}`,
        error?.message || error,
      )
    }
  }

  async sellAll(tokens: Array<LiquidationAsset>, callback: (info: any) => void) {
    for (const token of tokens) {
      let info: any = {}

      try {
        const response = await this.onSwapToken({
          sourceToken: token,
          network: token.network,
        })
        info.response = response

        if (response?.success && this._config.account) {
          const {
            inputToken,
            outputToken,
            network,
            sourceAmount,
            destAmount,
            srcUSD,
            destUSD,
            txHash,
          } = response

          const buybackItem = addBuyBackItem(this._config.account, {
            inputToken: {
              symbol: inputToken.symbol || token.symbol,
              name: inputToken.name || token.name,
              address: inputToken.address || token.address,
              decimals: inputToken.decimals || token.decimals,
              chainId: network.chainId,
              networkName: network.name,
              isNative: token.isNative || false,
            },
            outputToken: {
              symbol: outputToken.symbol,
              name: outputToken.name || outputToken.symbol,
              address: outputToken.address,
              decimals: outputToken.decimals,
              chainId: network.chainId,
              networkName: network.name,
              isNative: outputToken.isNative,
            },
            soldAmountUSD: srcUSD,
            receivedAmountUSD: destUSD,
            buybackAmountUSD: srcUSD,
            soldAmount: sourceAmount,
            receivedAmount: destAmount,
            txHash,
          })

          info.buybackItem = buybackItem
        }
      } catch (_) {}
      info.loadingMessage = `${token.network.chainId}.${token.symbol}`
      callback(info)
    }
  }
  getFeeAccount(mintAddress: string): TRefFee {
    const refCode = getRefCode()
    const refCodeJson = getPartnerInfoFromJSON(refCode || undefined)

    try {
      const [_feeAccount] = PublicKey.findProgramAddressSync(
        [
          Buffer.from('referral_ata'),
          new PublicKey(refCodeJson.solWalletAddress).toBuffer(), // your referral account public key
          new PublicKey(mintAddress).toBuffer(), // the token mint, output mint for ExactIn, input mint for ExactOut.
        ],
        new PublicKey(jupiterRefProgramId), // the Referral Program
      )
      return { account: _feeAccount, bps: refCodeJson.feeBps, tokenMint: mintAddress }
    } catch (error) {
      console.log('error when call getFeeAccount', mintAddress, error)
    }
  }

  async buyBackItem(item: BuyBackItem, ssCb: (swapHash: string) => void, errCb: () => void) {
    if (!this.address) return
    try {
      const inputMint = item.outputToken.address
      const outputMint = item.inputToken.address

      const tokenMetadata = await getJupiterTokens()
      const inputMeta = tokenMetadata.find((token) => token.address === inputMint)
      const inputToken: LiquidationAsset = {
        address: inputMint,
        symbol: inputMeta?.symbol || item.outputToken.symbol,
        name: inputMeta?.name || item.outputToken.name,
        decimals: inputMeta?.decimals || item.outputToken.decimals,
        network: {
          ...(item.inputToken.networkName ? { name: item.inputToken.networkName } : {}),
          chainId: item.inputToken.chainId,
        } as NetworkConfig,
        isNative: item.outputToken.isNative,
        balance: '0',
        balanceString: '0',
        USDBalance: item.receivedAmountUSD,
        stringUSDBalance: item.receivedAmountUSD.toString(),
        contract_address: inputMint,
        cmcCoinId: 0,
        isSelected: true,
        loading: false,
        allocation: 0,
        price: '',
        error: '',
        nativeCurrency: {
          name: 'Solana',
          symbol: 'SOL',
          cmcCoinId: 0,
        },
        stringBalance: '0',
      }

      const { balance, decimals } = await getSolanaTokenBalance(
        this.address,
        inputToken,
        this._config.solanaRpcUrl,
      )
      const balanceFormatted = new BigNumber(balance).dividedBy(10 ** decimals).toFixed()
      const estimatedTokenAmount =
        item.buybackAmountUSD / (item.receivedAmountUSD / Number(item.receivedAmount))
      const actualTokenAmount =
        Number(balanceFormatted) < estimatedTokenAmount
          ? Number(balanceFormatted)
          : estimatedTokenAmount

      if (actualTokenAmount <= 0 || Number(balanceFormatted) <= 0) {
        throw new Error(
          `Insufficient ${item.outputToken.symbol} balance for buyback. Have ${Number(
            balanceFormatted,
          ).toFixed(6)} ${item.outputToken.symbol}`,
        )
      }

      const amountInBaseUnits = new BigNumber(actualTokenAmount)
        .multipliedBy(10 ** inputToken.decimals)
        .integerValue(BigNumber.ROUND_FLOOR)
        .toFixed(0)
      const feeInfo = this.getFeeAccount(inputMint)

      const quote = await getSolanaQuote({
        inputMint,
        outputMint,
        amount: amountInBaseUnits,
        slippageBps: DEFAULT_SLIPPAGE_BPS,
        platformFeeBps: feeInfo.bps,
      })

      if (!quote) {
        throw new Error('Failed to get buyback quote')
      }

      const swapResponse = await getSolanaSwapTransaction({
        quote,
        userPublicKey: this._config.account,
        feeAccount: feeInfo.account,
      })

      const signature = await this.signAndSendSolanaTransaction(swapResponse, feeInfo)
      updateBuyBackItemTxHash(this.address, item.id, signature)
      ssCb(signature)
    } catch (error) {
      errCb()
    }
  }

  loadCache() {
    if (this.address) {
      this.accountState = getCachedTokens(this.address)
    }
    return this.accountState
  }
  async checkATAExist(address: PublicKey, connection: Connection): Promise<boolean> {
    const ataInformation = await connection.getParsedAccountInfo(address)
    return !!ataInformation?.value?.owner
  }
  createReferralATAInstruction(
    payer: PublicKey,
    associatedAccountToken: PublicKey,
    receiverAccount: PublicKey,
    tokenPublicKey: PublicKey,
    tokenOwner: PublicKey,
  ): TransactionInstruction {
    const JUPITER_PROJECT = new PublicKey(jupiterProjectPublicKey)
    const programId = new PublicKey(jupiterRefProgramId)
    function getAnchorInstructionDiscriminator(name: string) {
      const preimage = `global:${name}`
      const hash = sha256(new TextEncoder().encode(preimage))
      return Buffer.from(hash.slice(0, 8))
    }

    const instructionData = getAnchorInstructionDiscriminator('initialize_referral_token_account') // returns Buffer

    // STEP 3: Create the transaction instruction
    const keys = [
      { pubkey: payer, isSigner: true, isWritable: true },
      { pubkey: JUPITER_PROJECT, isSigner: false, isWritable: false },
      { pubkey: receiverAccount, isSigner: false, isWritable: true },
      { pubkey: associatedAccountToken, isSigner: false, isWritable: true },
      { pubkey: tokenPublicKey, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: tokenOwner, isSigner: false, isWritable: false },
    ]
    return new TransactionInstruction({
      programId,
      keys,
      data: Buffer.concat([instructionData]) as any, // just the 8-byte discriminator
    })
  }
  async feeInstructionCheck(feeAccount: PublicKey, tokenMint: string) {
    const instructions: TransactionInstruction[] = []
    const connection = new Connection(this._config.solanaRpcUrl || DEFAULT_SOLANA_RPC, 'confirmed')
    const walletPublicKey = new PublicKey(this.address)
    const isExist = await this.checkATAExist(feeAccount, connection)
    console.log('isExist', isExist)
    console.log('tokenMint', tokenMint)
    if (!isExist) {
      const mintInfo = await connection.getParsedAccountInfo(new PublicKey(tokenMint))
      instructions.push(
        this.createReferralATAInstruction(
          walletPublicKey,
          feeAccount,
          new PublicKey(feeAccount),
          new PublicKey(tokenMint),
          new PublicKey(mintInfo?.value?.owner ?? ''),
        ),
      )
    }
    console.log('instructions', instructions)
    return instructions
  }
  private async signAndSendSolanaTransaction(tx: any, feeInfo: TRefFee) {
    if (!this.address) {
      throw new Error('Solana wallet not available')
    }
    const signerPubkey = new PublicKey(this.address)
    const connection = new Connection(this._config.solanaRpcUrl || DEFAULT_SOLANA_RPC, 'confirmed')
    const instruction = await this.feeInstructionCheck(feeInfo.account, feeInfo.tokenMint)
    const {
      setupInstructions,
      swapInstruction: swapInstructionPayload, // The actual swap instruction.
      addressLookupTableAddresses, // The lookup table addresses that you can use if you are using versioned transaction.
      cleanupInstruction,
    } = tx
    console.log({
      setupInstructions,
      swapInstructionPayload,
      addressLookupTableAddresses,
      cleanupInstruction,
    })
    // versionedTransaction.
    const deserializeInstruction = (instruction: any) => {
      return new TransactionInstruction({
        programId: new PublicKey(instruction.programId),
        keys: instruction.accounts.map((key: any) => ({
          pubkey: new PublicKey(key.pubkey),
          isSigner: key.isSigner,
          isWritable: key.isWritable,
        })),
        data: Buffer.from(instruction.data, 'base64') as any,
      })
    }
    const getAddressLookupTableAccounts = async (
      keys: string[],
    ): Promise<AddressLookupTableAccount[]> => {
      const addressLookupTableAccountInfos = await connection.getMultipleAccountsInfo(
        keys.map((key) => new PublicKey(key)),
      )

      return addressLookupTableAccountInfos.reduce((acc, accountInfo, index) => {
        const addressLookupTableAddress = keys[index]
        if (accountInfo) {
          const addressLookupTableAccount = new AddressLookupTableAccount({
            key: new PublicKey(addressLookupTableAddress),
            state: AddressLookupTableAccount.deserialize(accountInfo.data),
          })
          acc.push(addressLookupTableAccount)
        }

        return acc
      }, new Array<AddressLookupTableAccount>())
    }
    const addressLookupTableAccounts: AddressLookupTableAccount[] = []

    addressLookupTableAccounts.push(
      ...(await getAddressLookupTableAccounts(addressLookupTableAddresses)),
    )
    const blockhash = (await connection.getLatestBlockhash()).blockhash

    const messageV0 = new TransactionMessage({
      payerKey: signerPubkey,
      recentBlockhash: blockhash,
      instructions: [
        ...instruction,
        ...setupInstructions.map(deserializeInstruction),
        deserializeInstruction(swapInstructionPayload),
        deserializeInstruction(cleanupInstruction),
      ],
    }).compileToV0Message(addressLookupTableAccounts)
    const latestTransaction = new VersionedTransaction(messageV0)

    if (this._config.solanaWallet.signAndSendTransaction) {
      const result = await this._config.solanaWallet.signAndSendTransaction(latestTransaction, {
        connection,
      })
      const signature = typeof result === 'string' ? result : result?.signature
      if (!signature) {
        throw new Error('No signature returned from wallet')
      }
      return signature
    }

    const signed = await this._config.solanaWallet.signTransaction(tx)
    const raw = (signed as VersionedTransaction).serialize()
    const signature = await connection.sendRawTransaction(raw)
    const latestBlockhash = await connection.getLatestBlockhash()
    await connection.confirmTransaction(
      {
        signature,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
      },
      'confirmed',
    )
    return signature
  }
}
